'use client';

import { useMemo, useCallback } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import { useRouter } from '@/i18n/routing';
import { Loader2, Package, Pencil, Trash2 } from 'lucide-react';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { SelectAllBanner } from '@/components/select-all-banner';
import {
  operatorsApi,
  operatorKeys,
  type Operator,
  type OperatorsListParams
} from '@/features/operators';
import {
  devicesApi,
  deviceKeys,
  type Device,
  type DevicesListParams
} from '@/features/devices';
import { handoverScanningQueries } from '../services/handover-scanning.queries';
import { useDeleteAssignmentMutation } from '../services/handover-scanning.mutations';
import { HANDOVER_SCANNING_HASH } from '../constants';
import { WorkloadTableActionBar } from './workload-table-action-bar';
import { WorkloadCard } from './workload-card';
import { WorkloadCompactRow } from './workload-compact-row';
import { AssignmentStatusBadge } from './assignment-status-badge';
import { EditAssignmentDialog } from './edit-assignment-dialog';
import type {
  AssignmentStatus,
  WorkloadAssignment,
  WorkloadListParams
} from '../types';

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-teal-500'
];

function getAvatarColor(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function WorkloadListing() {
  const t = useTranslations('handoverScanning');
  const locale = useLocale();
  const router = useRouter();
  const [view, setView] = useDataView('handover-scanning');
  // Overlay open-state lives in the URL hash (#edit/<id>, #delete/<id>) so
  // overlays are deep-linkable and close on browser Back. Destructure the
  // stable callbacks so they can go in hook dep arrays without tripping the
  // React Compiler's manual-memoization check.
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(HANDOVER_SCANNING_HASH.edit);
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(HANDOVER_SCANNING_HASH.delete);

  const mapOperatorOption = useCallback(
    (op: Operator) => ({
      value: String(op.id),
      label: op.name ?? `Operator #${op.id}`
    }),
    []
  );

  const mapScannerOption = useCallback(
    (d: Device) => ({
      value: String(d.id),
      label: d.serial_number ?? d.name
    }),
    []
  );

  const operatorInfinite = useInfiniteSelect<Operator, OperatorsListParams>({
    queryKey: operatorKeys.lists(),
    queryFn: (params) =>
      operatorsApi.getAll({ ...params, availability: ['available'] }),
    mapOption: mapOperatorOption
  });

  const scannerInfinite = useInfiniteSelect<Device, DevicesListParams>({
    queryKey: deviceKeys.lists(),
    queryFn: (params) => devicesApi.getAll({ ...params, status: ['online'] }),
    mapOption: mapScannerOption
  });

  const columns: ColumnDef<WorkloadAssignment>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    {
      id: 'container_number',
      accessorFn: (row) => row.scanning_container?.container_number ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('containerNumberColumn')}
        />
      ),
      cell: ({ row, column }) => {
        const value = row.original.scanning_container?.container_number;
        return (
          <span className='font-mono text-sm font-medium'>
            {value ? (
              <HighlightMatch
                text={value}
                query={getColumnSearchTerms(column)}
              />
            ) : (
              '—'
            )}
          </span>
        );
      },
      enableColumnFilter: true,
      meta: {
        label: t('searchPlaceholder'),
        variant: 'text' as const,
        placeholder: t('searchPlaceholder'),
        permission: 'handover_scanning.column.containerNumberColumn'
      }
    },
    {
      id: 'status',
      accessorFn: (row) => row.status ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('status')} />
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        if (!status) {
          return <span className='text-muted-foreground text-sm'>—</span>;
        }
        return <AssignmentStatusBadge status={status} />;
      },
      enableColumnFilter: true,
      meta: {
        label: t('status'),
        variant: 'multiSelect' as const,
        options: [
          { value: 'pending', label: t('status_pending') },
          { value: 'confirmed', label: t('status_confirmed') },
          { value: 'in_progress', label: t('status_in_progress') },
          { value: 'completed', label: t('status_completed') }
        ],
        permission: 'handover_scanning.column.status'
      }
    },
    {
      id: 'device',
      accessorFn: (row) => row.device?.serial_number ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('scannerNumberColumn')}
        />
      ),
      cell: ({ row }) => (
        <span className='font-mono text-sm'>
          {row.original.device?.serial_number ?? '—'}
        </span>
      ),
      enableColumnFilter: true,
      meta: {
        label: t('scannerNumberColumn'),
        variant: 'infiniteMultiSelect' as const,
        permission: 'handover_scanning.column.scannerNumberColumn'
      }
    },
    {
      id: 'operator',
      accessorFn: (row) => row.operator?.name ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('operatorColumn')} />
      ),
      cell: ({ row }) => {
        const item = row.original;
        const operatorName = item.operator?.name ?? '';
        if (!operatorName) {
          return <span className='text-muted-foreground text-sm'>—</span>;
        }
        return (
          <div className='flex items-center gap-2'>
            <Avatar className='h-8 w-8 shrink-0'>
              <AvatarFallback
                className={cn(
                  'text-xs font-bold text-white',
                  getAvatarColor(item.operator_id)
                )}
              >
                {getInitials(operatorName)}
              </AvatarFallback>
            </Avatar>
            <span className='text-sm'>{operatorName}</span>
          </div>
        );
      },
      enableColumnFilter: true,
      meta: {
        label: t('operatorColumn'),
        variant: 'infiniteMultiSelect' as const,
        permission: 'handover_scanning.column.operatorColumn'
      }
    },
    {
      id: 'pages_progress',
      accessorFn: (row) => row.scanned_pages ?? 0,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('pagesProgressColumn')}
        />
      ),
      cell: ({ row }) => {
        const total = row.original.total_pages ?? 0;
        const scanned = Math.min(
          row.original.scanned_pages ?? 0,
          total || Number.MAX_SAFE_INTEGER
        );
        const remaining = Math.max(total - scanned, 0);
        const percent =
          total > 0 ? Math.min(100, Math.round((scanned / total) * 100)) : 0;
        if (total === 0) {
          return <span className='text-muted-foreground text-sm'>—</span>;
        }
        return (
          <div className='flex w-40 flex-col gap-1'>
            <div className='flex items-center justify-between text-xs font-medium'>
              <span className='font-mono'>
                {scanned} / {total}
              </span>
              <span className='text-muted-foreground'>{percent}%</span>
            </div>
            <Progress value={percent} className='h-1.5' />
            <span className='text-muted-foreground text-[11px]'>
              {t('pagesRemaining', { count: remaining })}
            </span>
          </div>
        );
      },
      enableSorting: true,
      meta: {
        label: t('pagesProgressColumn'),
        permission: 'handover_scanning.column.pagesProgressColumn'
      }
    },
    // {
    //   id: 'box',
    //   accessorFn: (row) => row.scanning_container?.box?.box_number ?? '',
    //   header: ({ column }) => (
    //     <DataTableColumnHeader column={column} title={t('boxColumn')} />
    //   ),
    //   cell: ({ row }) => {
    //     const boxNumber = row.original.scanning_container?.box?.box_number;
    //     if (!boxNumber) {
    //       return <span className='text-muted-foreground text-sm'>—</span>;
    //     }
    //     return (
    //       <Badge variant='secondary' className='gap-1'>
    //         <Package className='h-3.5 w-3.5' />
    //         {boxNumber}
    //       </Badge>
    //     );
    //   },
    //   meta: { label: t('boxColumn') }
    // },
    {
      id: 'actions',
      header: () => <div className='text-end'>{t('actions')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className='flex items-center justify-end gap-2'>
            <Button
              variant='ghost'
              size='icon'
              className='bg-muted text-muted-foreground hover:bg-muted/80 h-8 w-8 rounded-full'
              onClick={(e) => {
                e.stopPropagation();
                openEdit(String(item.id));
              }}
            >
              <Pencil className='h-4 w-4' />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8 rounded-full bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
              onClick={(e) => {
                e.stopPropagation();
                openDelete(String(item.id));
              }}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false
    }
  ];

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'container_number',
    parseAsString.withDefault('')
  );
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlOperator] = useQueryState(
    'operator',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlDevice] = useQueryState(
    'device',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<WorkloadAssignment>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<WorkloadAssignment>[])
  );

  const queryParams = useMemo<WorkloadListParams>(() => {
    const params: WorkloadListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) {
      params.search = urlSearch;
    }
    if (urlStatus.length > 0) {
      params.status = urlStatus as AssignmentStatus[];
    }
    if (urlOperator.length > 0) {
      const ids = urlOperator.map(Number).filter((n) => n > 0);
      if (ids.length > 0) params.operator_id = ids;
    }
    if (urlDevice.length > 0) {
      const ids = urlDevice.map(Number).filter((n) => n > 0);
      if (ids.length > 0) params.device_id = ids;
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';
    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    urlStatus,
    urlOperator,
    urlDevice,
    urlSorting
  ]);

  const { data, isPending, isPlaceholderData, dataUpdatedAt } = useQuery(
    handoverScanningQueries.list(queryParams)
  );
  const deleteMutation = useDeleteAssignmentMutation();

  const assignments = (data?.data ?? []) as WorkloadAssignment[];
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  const editTarget = editOpen
    ? (assignments.find((a) => String(a.id) === editId) ?? null)
    : null;
  const deleteTarget = deleteOpen
    ? (assignments.find((a) => String(a.id) === deleteId) ?? null)
    : null;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<WorkloadAssignment>({
    columns: filteredColumns,
    pageKey: 'handover-scanning',
    data: assignments,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('containerNumberColumn'), value: 'container_number' },
      { label: t('scannerNumberColumn'), value: 'device' },
      { label: t('operatorColumn'), value: 'operator' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  const getBulkPayload = useCallback(():
    { ids: number[] } | { all: true; filters: Partial<WorkloadListParams> } => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { all: true, filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  const handleRowClick = useCallback(
    (row: WorkloadAssignment) => {
      router.push(`/dashboard/handover-scanning/${row.id}`);
    },
    [router]
  );

  const handleDeleteRow = useCallback(
    (assignment: WorkloadAssignment) => openDelete(String(assignment.id)),
    [openDelete]
  );

  const selectedCount = selectAll.isAllPagesSelected
    ? (pagination?.total ?? 0)
    : table.getFilteredSelectedRowModel().rows.length;

  return (
    <div className='space-y-4'>
      {/* Toolbar: View Toggle + Sort Control */}
      <div className='flex items-center justify-end gap-2'>
        {view !== 'table' && (
          <SortControl
            options={sortOptions}
            sorting={tableState.sorting}
            onSortingChange={(s) => table.setSorting(s)}
          />
        )}
        <DataViewToggle
          value={view}
          onValueChange={setView}
          pageKey='handover-scanning'
        />
      </div>

      <DataTableToolbar
        table={table}
        viewFilterPermission='handover_scanning.view_filter'
        infiniteConfigs={{
          operator: operatorInfinite,
          device: scannerInfinite
        }}
      />

      {isPending ? (
        <ListingSkeleton view={view} />
      ) : assignments.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Package className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noAssignments')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {assignments.map((item) => (
              <WorkloadCard
                key={item.id}
                assignment={item}
                onDelete={handleDeleteRow}
                onClick={handleRowClick}
                avatarColor={getAvatarColor(item.operator_id)}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
          {/* Last update timestamp */}
          {dataUpdatedAt > 0 && (
            <p className='text-muted-foreground text-xs'>
              {t('lastUpdate')}:{' '}
              {new Date(dataUpdatedAt).toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </p>
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {assignments.map((item) => (
              <WorkloadCompactRow
                key={item.id}
                assignment={item}
                onDelete={handleDeleteRow}
                onClick={handleRowClick}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
          {/* Last update timestamp */}
          {dataUpdatedAt > 0 && (
            <p className='text-muted-foreground text-xs'>
              {t('lastUpdate')}:{' '}
              {new Date(dataUpdatedAt).toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </p>
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={assignments.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            onRowClick={handleRowClick}
            actionBar={
              <WorkloadTableActionBar
                selectedCount={selectedCount}
                getBulkPayload={getBulkPayload}
                onSuccess={handleBulkComplete}
              />
            }
          ></DataTable>

          {/* Last update timestamp */}
          {dataUpdatedAt > 0 && (
            <p className='text-muted-foreground text-xs'>
              {t('lastUpdate')}:{' '}
              {new Date(dataUpdatedAt).toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </p>
          )}
        </div>
      )}

      {/* Single delete confirmation — #delete/<id> */}
      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', {
                number: deleteTarget?.assignment_number ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {deleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit assignment dialog — #edit/<id> */}
      <EditAssignmentDialog
        assignment={editOpen ? editTarget : null}
        onOpenChange={closeEdit}
      />
    </div>
  );
}
